import { Sketch } from "@uiw/react-color";
import {
  Anchor,
  AnnotationInfo,
  CheckCircleBroken,
  Colors,
  EyeOff,
  HelpCircle,
  PaintPour,
  Sale03,
  Save01,
  Star06,
  Tag01,
  Trash02,
  UploadCloud02,
} from "@untitledui/icons";
import { Image, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { BriefCategoryItem, BriefContent } from "@/api/smart-briefs";
import { Button } from "@/components/base/buttons/button";
import { TextAreaBase } from "@/components/base/textarea/textarea";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { VideoEmbed } from "@/pages/videos/video-embed";
import type { VideoExample } from "./SmartBriefContent";

// Standard category headers used in briefs
// Some briefs use suffixes like "(Archetypes)" or "(Tactics)", others don't
const CATEGORY_HEADERS = {
  VIDEO_STYLE: ["Video Style (Archetypes)", "Video Style"],
  HOOK: ["Hook"],
  SELLING_POINTS: ["Selling Points"],
  SALES_TACTICS: ["Sales Tactics (Tactics)", "Sales Tactics"],
} as const;

// Predefined tag suggestions for each category
const TAG_SUGGESTIONS: Record<string, { do: string[]; dont: string[] }> = {
  "Video Style": {
    do: [
      "UGC style",
      "Authentic",
      "Educational",
      "Tutorial",
      "Before/After",
      "Lifestyle",
      "Talking head",
      "Product demo",
      "Unboxing",
      "Review",
      "Day in my life",
      "Story time",
      "GRWM",
      "POV",
    ],
    dont: [
      "Overly polished",
      "Too scripted",
      "Hard sell",
      "Infomercial style",
      "Stock footage",
      "Text-only",
      "Low quality",
      "Poor lighting",
    ],
  },
  Hook: {
    do: [
      "Question hook",
      "Curiosity gap",
      "Bold claim",
      "Relatable problem",
      "Transformation tease",
      "Secret reveal",
      "Controversy",
      "Number/Stat",
      "Story start",
      "Direct address",
    ],
    dont: [
      "Generic intro",
      "Slow start",
      "No hook",
      "Clickbait",
      "Misleading",
      "Too long",
      "Boring opener",
      "Off-topic start",
    ],
  },
  "Selling Points": {
    do: [
      "Benefits first",
      "Social proof",
      "Problem/Solution",
      "Unique features",
      "Value proposition",
      "Testimonials",
      "Results focus",
      "Emotional appeal",
      "Scarcity",
      "Comparison",
    ],
    dont: [
      "Feature dump",
      "No benefits",
      "Generic claims",
      "Unclear value",
      "Too technical",
      "No differentiation",
      "Weak CTA",
      "Confusing message",
    ],
  },
  "Sales Tactics": {
    do: [
      "Soft CTA",
      "Link in bio",
      "Limited time",
      "FOMO",
      "Bundle deal",
      "Discount code",
      "Free shipping",
      "Money-back guarantee",
      "Try it yourself",
      "Comment to buy",
    ],
    dont: [
      "Hard sell",
      "Pushy CTA",
      "Multiple CTAs",
      "No urgency",
      "Unclear next step",
      "Too salesy",
      "Desperate tone",
      "Spam tactics",
    ],
  },
};

interface SmartBriefEditInnerProps {
  // Data
  content: BriefContent;
  title: string;
  bannerColor: string;
  logoUrl?: string;
  examples: VideoExample[];
  createdAt: string;
  productThumbnail?: string;
  /** True if user has set a custom thumbnail (not the auto-fetched one) */
  hasCustomThumbnail?: boolean;

  // Change handlers
  onContentChange: (content: BriefContent) => void;
  onTitleChange: (title: string) => void;
  onBannerColorChange: (color: string) => void;
  onLogoUpload?: (file: File) => void;
  onLogoDelete?: () => void;
  onProductThumbnailUpload?: (file: File) => void;
  onProductThumbnailDelete?: () => void;
  /** Reset thumbnail to original (auto-fetched from product data) */
  onProductThumbnailReset?: () => void;

  // Actions
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  onPreview?: () => void;

  // State
  isSaving: boolean;
  isDeleting: boolean;

  /** When true, shows draft mode UI (no delete/preview, different button labels) */
  isDraftMode?: boolean;
}

// Tag Input component with dropdown suggestions
const TagInputWithSuggestions = ({
  value,
  onChange,
  onAdd,
  suggestions,
  existingTags,
  placeholder,
  variant,
}: {
  value: string;
  onChange: (value: string) => void;
  onAdd: (tag: string) => void;
  suggestions: string[];
  existingTags: string[];
  placeholder: string;
  variant: "do" | "dont";
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on input and exclude already added tags
  const filteredSuggestions = suggestions.filter(
    (s) => s.toLowerCase().includes(value.toLowerCase()) && !existingTags.includes(s),
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredSuggestions.length) {
        // Add highlighted suggestion
        onAdd(filteredSuggestions[highlightedIndex]);
        onChange("");
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      } else if (value.trim()) {
        // Add custom tag
        onAdd(value.trim());
        onChange("");
        setShowSuggestions(false);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < filteredSuggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }
  };

  const handleSelectSuggestion = (suggestion: string) => {
    onAdd(suggestion);
    onChange("");
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <div className="relative flex-1">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setShowSuggestions(true);
          setHighlightedIndex(-1);
        }}
        onFocus={() => setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full min-w-24 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 placeholder:text-placeholder"
      />
      {/* Dropdown suggestions */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute left-0 top-full mt-1 w-64 max-h-48 overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 z-50"
        >
          {filteredSuggestions.map((suggestion, idx) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              onMouseEnter={() => setHighlightedIndex(idx)}
              className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                idx === highlightedIndex
                  ? variant === "do"
                    ? "bg-green-50 text-green-700"
                    : "bg-red-50 text-red-700"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Guideline Section component for editing
const GuidelineSection = ({
  icon,
  title,
  category,
  description,
  onDescriptionChange,
  doPatterns,
  onDoPatternsChange,
  dontPatterns,
  onDontPatternsChange,
}: {
  icon: React.ReactNode;
  title: string;
  category: string;
  description: string;
  onDescriptionChange: (value: string) => void;
  doPatterns: string[];
  onDoPatternsChange: (patterns: string[]) => void;
  dontPatterns: string[];
  onDontPatternsChange: (patterns: string[]) => void;
}) => {
  const [newDoTag, setNewDoTag] = useState("");
  const [newDontTag, setNewDontTag] = useState("");
  const [dismissedDoTags, setDismissedDoTags] = useState<string[]>([]);
  const [dismissedDontTags, setDismissedDontTags] = useState<string[]>([]);

  // Track original tags from LLM (captured on first render)
  const originalDoTagsRef = useRef<Set<string>>(new Set(doPatterns));
  const originalDontTagsRef = useRef<Set<string>>(new Set(dontPatterns));

  // Get suggestions for this category
  const suggestions = TAG_SUGGESTIONS[category] || { do: [], dont: [] };

  const handleAddDoTag = (tag: string) => {
    if (tag && !doPatterns.includes(tag)) {
      onDoPatternsChange([...doPatterns, tag]);
      // Remove from dismissed if it was there
      setDismissedDoTags((prev) => prev.filter((t) => t !== tag));
    }
  };

  const handleAddDontTag = (tag: string) => {
    if (tag && !dontPatterns.includes(tag)) {
      onDontPatternsChange([...dontPatterns, tag]);
      // Remove from dismissed if it was there
      setDismissedDontTags((prev) => prev.filter((t) => t !== tag));
    }
  };

  const handleRemoveDoTag = (tagToRemove: string) => {
    onDoPatternsChange(doPatterns.filter((tag) => tag !== tagToRemove));
    // Only add to dismissed if it was an original LLM tag
    if (originalDoTagsRef.current.has(tagToRemove)) {
      setDismissedDoTags((prev) => [...prev, tagToRemove]);
    }
  };

  const handleRemoveDontTag = (tagToRemove: string) => {
    onDontPatternsChange(dontPatterns.filter((tag) => tag !== tagToRemove));
    // Only add to dismissed if it was an original LLM tag
    if (originalDontTagsRef.current.has(tagToRemove)) {
      setDismissedDontTags((prev) => [...prev, tagToRemove]);
    }
  };

  const handleRestoreDoTag = (tag: string) => {
    handleAddDoTag(tag);
  };

  const handleRestoreDontTag = (tag: string) => {
    handleAddDontTag(tag);
  };

  const hasDismissedTags = dismissedDoTags.length > 0 || dismissedDontTags.length > 0;

  return (
    <div className="w-full max-w-[768px] min-w-[560px] rounded-lg flex flex-col gap-4">
      {/* Header with icon, title, and help tooltip */}
      <div className="self-stretch flex flex-col gap-2">
        <div className="self-stretch flex items-center justify-between">
          <div className="flex items-start gap-2">
            {icon}
            <span className="text-base font-medium text-quaternary leading-6">{title}</span>
          </div>
          <Tooltip
            title="Auto-generated"
            description={`This ${title.toLowerCase()} has been auto-created based on the best performing ${title.toLowerCase()} in the highest performing videos.`}
            placement="left"
          >
            <TooltipTrigger>
              <HelpCircle className="w-4 h-4 text-quaternary hover:text-tertiary cursor-help transition-colors" />
            </TooltipTrigger>
          </Tooltip>
        </div>
      </div>

      {/* Content area */}
      <div className="self-stretch flex flex-col gap-4">
        {/* Description textarea - fixed height */}
        <TextAreaBase
          value={description === "No major insight for this category." ? "" : description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder=""
          className="self-stretch h-40 resize-none"
        />

        {/* Do tags (green) */}
        <div className="self-stretch flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-tertiary">Do</label>
            <Tooltip
              title="Tag suggestions"
              description="Add tags to highlight what works. Green tags are recommendations to include in videos."
              placement="top"
            >
              <TooltipTrigger>
                <HelpCircle className="w-3.5 h-3.5 text-quaternary hover:text-tertiary cursor-help transition-colors" />
              </TooltipTrigger>
            </Tooltip>
          </div>
          <div className="self-stretch px-3 py-2 bg-primary rounded-lg ring-1 ring-primary ring-inset flex items-center gap-2 focus-within:ring-2 focus-within:ring-brand">
            <div className="flex-1 flex items-center gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                {doPatterns.map((pattern, idx) => (
                  <div
                    key={`do-${idx}`}
                    className="pl-2.5 pr-1 py-0.5 bg-green-50 rounded-full outline outline-1 outline-offset-[-1px] outline-green-200 flex items-center gap-0.5"
                  >
                    <span className="text-center text-green-700 text-sm font-medium leading-5">{pattern}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveDoTag(pattern)}
                      className="p-0.5 rounded-full hover:bg-green-100 transition-colors"
                    >
                      <X className="w-3 h-3 text-green-400" />
                    </button>
                  </div>
                ))}
                <TagInputWithSuggestions
                  value={newDoTag}
                  onChange={setNewDoTag}
                  onAdd={handleAddDoTag}
                  suggestions={suggestions.do}
                  existingTags={doPatterns}
                  placeholder="Add do..."
                  variant="do"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Don't tags (red) */}
        <div className="self-stretch flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-tertiary">Don't</label>
            <Tooltip
              title="Tag suggestions"
              description="Add tags to highlight what to avoid. Red tags are things to avoid in videos."
              placement="top"
            >
              <TooltipTrigger>
                <HelpCircle className="w-3.5 h-3.5 text-quaternary hover:text-tertiary cursor-help transition-colors" />
              </TooltipTrigger>
            </Tooltip>
          </div>
          <div className="self-stretch px-3 py-2 bg-primary rounded-lg ring-1 ring-primary ring-inset flex items-center gap-2 focus-within:ring-2 focus-within:ring-brand">
            <div className="flex-1 flex items-center gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                {dontPatterns.map((pattern, idx) => (
                  <div
                    key={`dont-${idx}`}
                    className="pl-2.5 pr-1 py-0.5 bg-red-50 rounded-full outline outline-1 outline-offset-[-1px] outline-red-200 flex items-center gap-0.5"
                  >
                    <span className="text-center text-red-700 text-sm font-medium leading-5">{pattern}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveDontTag(pattern)}
                      className="p-0.5 rounded-full hover:bg-red-100 transition-colors"
                    >
                      <X className="w-3 h-3 text-red-400" />
                    </button>
                  </div>
                ))}
                <TagInputWithSuggestions
                  value={newDontTag}
                  onChange={setNewDontTag}
                  onAdd={handleAddDontTag}
                  suggestions={suggestions.dont}
                  existingTags={dontPatterns}
                  placeholder="Add don't..."
                  variant="dont"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Dismissed recommendations - only show if there are dismissed tags */}
        {hasDismissedTags && (
          <div className="self-stretch inline-flex flex-col justify-start items-start gap-2">
            <div className="inline-flex justify-start items-center gap-1.5">
              <EyeOff className="w-4 h-4 text-quaternary" />
              <span className="text-secondary text-sm font-medium leading-5">Dismissed recommendations</span>
              <Tooltip
                title="Restore tags"
                description="Click a tag to restore it back to the Do or Don't list."
                placement="top"
              >
                <TooltipTrigger>
                  <HelpCircle className="w-4 h-4 text-quaternary hover:text-tertiary cursor-help transition-colors" />
                </TooltipTrigger>
              </Tooltip>
            </div>
            <div className="self-stretch p-3 bg-brand-primary_alt rounded-md flex flex-col justify-start items-start gap-2.5">
              {dismissedDoTags.map((tag, idx) => (
                <button
                  key={`dismissed-do-${idx}`}
                  type="button"
                  onClick={() => handleRestoreDoTag(tag)}
                  className="pl-2.5 pr-2 py-0.5 bg-white rounded-full outline outline-1 outline-offset-[-1px] outline-gray-200 inline-flex justify-start items-center gap-1 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <span className="text-center text-gray-700 text-sm font-medium leading-5">{tag}</span>
                  <X className="w-3 h-3 text-gray-500" />
                </button>
              ))}
              {dismissedDontTags.map((tag, idx) => (
                <button
                  key={`dismissed-dont-${idx}`}
                  type="button"
                  onClick={() => handleRestoreDontTag(tag)}
                  className="pl-2.5 pr-2 py-0.5 bg-white rounded-full outline outline-1 outline-offset-[-1px] outline-gray-200 inline-flex justify-start items-center gap-1 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <span className="text-center text-gray-700 text-sm font-medium leading-5">{tag}</span>
                  <X className="w-3 h-3 text-gray-500" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const SmartBriefEditInner = ({
  content,
  title,
  bannerColor,
  logoUrl,
  examples,
  createdAt,
  productThumbnail,
  hasCustomThumbnail,
  onContentChange,
  onTitleChange,
  onBannerColorChange,
  onLogoUpload,
  onLogoDelete,
  onProductThumbnailUpload,
  onProductThumbnailDelete,
  onProductThumbnailReset,
  onSave,
  onCancel,
  onDelete,
  isSaving,
  isDeleting,
  isDraftMode = false,
}: SmartBriefEditInnerProps) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  // Clear thumbnail error when thumbnail is set
  useEffect(() => {
    if (productThumbnail && thumbnailError) {
      setThumbnailError(null);
    }
  }, [productThumbnail, thumbnailError]);

  // Validate and save
  const handleSaveWithValidation = () => {
    if (!productThumbnail) {
      setThumbnailError("Product thumbnail is required");
      return;
    }
    setThumbnailError(null);
    onSave();
  };

  // Helper to get item by category, with fallback for missing categories
  // Handles both normalized (doList) and raw (do_list) data
  // Also handles category name variants (e.g., "Video Style" vs "Video Style (Archetypes)")
  const getDoListItem = (categoryHeaders: readonly string[]): BriefCategoryItem => {
    const doList = content.doList || (content as any).do_list || [];
    // Try each variant of the category header
    for (const categoryHeader of categoryHeaders) {
      const item = doList.find((item: BriefCategoryItem) => item.category_header === categoryHeader);
      if (item) return item;
    }
    return { category_header: categoryHeaders[0], description: "", key_patterns: [] };
  };

  const getDontListItem = (categoryHeaders: readonly string[]): BriefCategoryItem => {
    const dontList = content.dontList || (content as any).dont_list || [];
    // Try each variant of the category header
    for (const categoryHeader of categoryHeaders) {
      const item = dontList.find((item: BriefCategoryItem) => item.category_header === categoryHeader);
      if (item) return item;
    }
    return { category_header: categoryHeaders[0], description: "", key_patterns: [] };
  };

  // Update do list item by category header (handles category name variants)
  const updateDoListByCategory = (categoryHeaders: readonly string[], field: string, value: string | string[]) => {
    const currentDoList = content.doList || (content as any).do_list || [];
    // Find the item using any variant of the category name
    let actualIndex = -1;
    for (const categoryHeader of categoryHeaders) {
      actualIndex = currentDoList.findIndex((item) => item.category_header === categoryHeader);
      if (actualIndex !== -1) break;
    }
    const categoryHeader = categoryHeaders[0]; // Use first variant for new items

    let updatedDoList: BriefCategoryItem[];
    if (actualIndex === -1) {
      // Category doesn't exist - add a new item
      const newItem = { category_header: categoryHeader, description: "", key_patterns: [], [field]: value };
      updatedDoList = [...currentDoList, newItem];
    } else {
      // Category exists - update it
      updatedDoList = [...currentDoList];
      updatedDoList[actualIndex] = { ...updatedDoList[actualIndex], [field]: value };
    }

    onContentChange({ ...content, doList: updatedDoList });
  };

  // Update dont list item by category header (handles category name variants)
  const updateDontListByCategory = (categoryHeaders: readonly string[], field: string, value: string | string[]) => {
    const currentDontList = content.dontList || (content as any).dont_list || [];
    // Find the item using any variant of the category name
    let actualIndex = -1;
    for (const categoryHeader of categoryHeaders) {
      actualIndex = currentDontList.findIndex((item) => item.category_header === categoryHeader);
      if (actualIndex !== -1) break;
    }
    const categoryHeader = categoryHeaders[0]; // Use first variant for new items

    let updatedDontList: BriefCategoryItem[];
    if (actualIndex === -1) {
      // Category doesn't exist - add a new item
      const newItem = { category_header: categoryHeader, description: "", key_patterns: [], [field]: value };
      updatedDontList = [...currentDontList, newItem];
    } else {
      // Category exists - update it
      updatedDontList = [...currentDontList];
      updatedDontList[actualIndex] = { ...updatedDontList[actualIndex], [field]: value };
    }

    onContentChange({ ...content, dontList: updatedDontList });
  };

  // Update content field
  const updateContentField = (field: keyof BriefContent, value: string) => {
    onContentChange({ ...content, [field]: value });
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onLogoUpload) {
      onLogoUpload(file);
    }
  };

  const triggerThumbnailUpload = () => {
    thumbnailInputRef.current?.click();
  };

  const handleThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onProductThumbnailUpload) {
      onProductThumbnailUpload(file);
    }
    // Reset the input so the same file can be selected again
    event.target.value = "";
  };

  return (
    <div className="w-full min-h-screen bg-white overflow-hidden">
      <div className="flex flex-col">
        {/* Header Section - Colored Background */}
        <div
          className="w-full self-stretch flex justify-center px-10 pt-12 pb-12"
          style={{ backgroundColor: bannerColor || "#F3F3F3" }}
        >
          <div className="w-full max-w-[1168px] flex flex-col gap-10">
            {/* Top Header with Action Buttons */}
            <div className="self-stretch flex flex-col gap-16">
              <div className="self-stretch flex flex-col gap-4">
                <div className="self-stretch flex justify-end items-center gap-3 flex-wrap">
                  {/* Action Buttons */}
                  <Button
                    color="secondary"
                    size="md"
                    onClick={onCancel}
                    iconLeading={<X className="h-5 w-5 text-quaternary" />}
                  >
                    Cancel
                  </Button>
                  {!isDraftMode && onDelete && (
                    <Button
                      color="secondary"
                      size="md"
                      onClick={onDelete}
                      disabled={isDeleting}
                      iconLeading={<Trash02 className="h-5 w-5 text-quaternary" />}
                    >
                      {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                  )}
                  <Button
                    size="md"
                    onClick={handleSaveWithValidation}
                    isDisabled={isSaving}
                    iconLeading={<Save01 className="h-5 w-5" />}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Logo Row with Color Picker */}
            <div className="w-full flex justify-between items-center">
              {/* Logo Section - With Logo or Empty State */}
              {logoUrl ? (
                <div className="px-3 py-2 bg-white/10 rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-900 flex items-center gap-4 overflow-hidden">
                  <img src={logoUrl} alt="Brand logo" className="h-8 w-auto max-w-32 object-contain" />
                  <button
                    type="button"
                    onClick={onLogoDelete}
                    className="w-8 h-8 p-1.5 rounded-md flex justify-center items-center hover:bg-white/20 transition-colors"
                  >
                    <Trash02 className="h-5 w-5 text-gray-900" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={triggerFileUpload}
                  className="p-3 bg-primary rounded-lg outline outline-1 outline-offset-[-1px] outline-primary inline-flex justify-center items-center gap-1 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex justify-center items-start gap-3">
                    <div className="w-10 h-10 relative bg-primary rounded-lg outline outline-1 outline-offset-[-1px] outline-primary overflow-hidden flex items-center justify-center">
                      <UploadCloud02 className="w-5 h-5 text-secondary" />
                    </div>
                    <div className="inline-flex flex-col justify-start items-start gap-1">
                      <span className="text-primary text-sm font-semibold leading-5">Click to upload Logo</span>
                      <span className="text-tertiary text-xs font-normal leading-4">
                        SVG, PNG or JPG (max. 48x500px)
                      </span>
                    </div>
                  </div>
                </button>
              )}

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                onChange={handleFileChange}
                className="hidden"
              />

              {/* Color Picker Button */}
              <div className="relative">
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  iconLeading={<PaintPour className="h-5 w-5 text-quaternary" />}
                >
                  {(bannerColor || "#F3F3F3").toUpperCase()}
                </Button>

                {/* Color Picker Popover */}
                {showColorPicker && (
                  <div className="absolute top-12 right-0 z-50">
                    <div className="fixed inset-0" onClick={() => setShowColorPicker(false)} />
                    <div className="relative">
                      <Sketch
                        color={bannerColor || "#F3F3F3"}
                        onChange={(color) => {
                          if (color && typeof color === "object" && "hex" in color && color.hex) {
                            onBannerColorChange(color.hex);
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="w-full flex justify-center px-10 py-16">
          <div className="w-full max-w-[1168px] inline-flex flex-col justify-start items-center gap-24 overflow-hidden">
            {/* Title, Date, Introduction Section */}
            <div className="self-stretch inline-flex justify-between items-start">
              {/* Left: Title, Date and Introduction */}
              <div className="flex-1 max-w-[768px] pr-16 inline-flex flex-col justify-start items-start gap-8">
                {/* Title and Date Group */}
                <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                  {/* Title Input */}
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    placeholder=""
                    className="self-stretch px-3.5 py-3 bg-primary rounded-lg ring-1 ring-primary ring-inset text-4xl font-normal text-primary leading-10 focus:outline-none focus:ring-2 focus:ring-brand"
                  />

                  {/* Created Date */}
                  <div className="justify-start text-placeholder text-base font-semibold leading-6">
                    Created Date:{" "}
                    {new Date(createdAt).toLocaleDateString("en-CA", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    })}
                  </div>
                </div>

                {/* Introduction Textarea */}
                <TextAreaBase
                  value={content.product || ""}
                  onChange={(e) => updateContentField("product", e.target.value)}
                  placeholder=""
                  className="self-stretch h-32 resize-none"
                />
              </div>

              {/* Right: Product Thumbnail */}
              <div className="shrink-0 flex flex-col items-center gap-2">
                <div className="relative">
                  {productThumbnail ? (
                    <>
                      <div
                        className="w-[210px] h-[210px] rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={triggerThumbnailUpload}
                      >
                        <img src={productThumbnail} alt="Product" className="w-full h-full object-cover" />
                      </div>
                      {onProductThumbnailDelete && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onProductThumbnailDelete();
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          className="absolute top-3 right-3 w-12 h-12 bg-tertiary rounded-full flex justify-center items-center hover:bg-quaternary transition-colors z-10"
                        >
                          <X className="h-6 w-6 text-brand-solid" strokeWidth={1.5} />
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={triggerThumbnailUpload}
                      className="w-[210px] h-[210px] p-4 bg-primary rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 flex flex-col justify-center items-center gap-3 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <UploadCloud02 className="w-6 h-6 text-secondary" />
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-primary text-sm font-semibold leading-5">Click to upload</span>
                        <span className="text-tertiary text-xs font-normal leading-4">PNG or JPG</span>
                      </div>
                    </button>
                  )}
                </div>
                {/* Reset to original button - only show when custom thumbnail is set */}
                {hasCustomThumbnail && onProductThumbnailReset && (
                  <button
                    type="button"
                    onClick={onProductThumbnailReset}
                    className="text-sm text-tertiary hover:text-primary underline transition-colors"
                  >
                    Reset to original
                  </button>
                )}
                {/* Validation error message */}
                {thumbnailError && <span className="text-sm text-error-primary">{thumbnailError}</span>}
                {/* Hidden file input for thumbnail */}
                <input
                  ref={thumbnailInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleThumbnailChange}
                  className="hidden"
                />
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-gray-200" />

            {/* Guidelines Section */}
            <div className="self-stretch inline-flex justify-start items-start gap-6">
              {/* Left Sidebar */}
              <div className="w-96 inline-flex flex-col justify-start items-start gap-8">
                <div className="w-72 justify-start text-primary text-xl font-medium leading-8">
                  Guidelines for videos that connect and convert
                </div>
                <div className="inline-flex justify-start items-start gap-2">
                  <div className="self-stretch inline-flex flex-col justify-start items-center gap-2">
                    <Star06 className="w-5 h-5 text-gray-700" />
                    <div className="w-1 flex-1 bg-quaternary rounded-md" />
                  </div>
                  <p className="w-72 text-base font-normal text-secondary leading-6 italic">
                    We've studied thousands of successful videos to identify proven patterns that increase your chances
                    of sales.
                  </p>
                </div>
              </div>

              {/* Right Content - Guidelines */}
              <div className="flex-1 inline-flex flex-col justify-start items-start gap-12">
                <div className="self-stretch inline-flex justify-start items-start gap-12">
                  <div className="flex-1 inline-flex flex-col justify-start items-start gap-12">
                    {/* Video Style */}
                    <GuidelineSection
                      icon={<Colors className="h-6 w-6 text-gray-400" />}
                      title="Video Style"
                      category="Video Style"
                      description={getDoListItem(CATEGORY_HEADERS.VIDEO_STYLE).description || ""}
                      onDescriptionChange={(value) =>
                        updateDoListByCategory(CATEGORY_HEADERS.VIDEO_STYLE, "description", value)
                      }
                      doPatterns={getDoListItem(CATEGORY_HEADERS.VIDEO_STYLE).key_patterns || []}
                      onDoPatternsChange={(patterns) =>
                        updateDoListByCategory(CATEGORY_HEADERS.VIDEO_STYLE, "key_patterns", patterns)
                      }
                      dontPatterns={getDontListItem(CATEGORY_HEADERS.VIDEO_STYLE).key_patterns || []}
                      onDontPatternsChange={(patterns) =>
                        updateDontListByCategory(CATEGORY_HEADERS.VIDEO_STYLE, "key_patterns", patterns)
                      }
                    />

                    <div className="w-full h-px bg-gray-200" />

                    {/* Hook */}
                    <GuidelineSection
                      icon={<Anchor className="h-6 w-6 text-gray-400" />}
                      title="Hook"
                      category="Hook"
                      description={getDoListItem(CATEGORY_HEADERS.HOOK).description || ""}
                      onDescriptionChange={(value) =>
                        updateDoListByCategory(CATEGORY_HEADERS.HOOK, "description", value)
                      }
                      doPatterns={getDoListItem(CATEGORY_HEADERS.HOOK).key_patterns || []}
                      onDoPatternsChange={(patterns) =>
                        updateDoListByCategory(CATEGORY_HEADERS.HOOK, "key_patterns", patterns)
                      }
                      dontPatterns={getDontListItem(CATEGORY_HEADERS.HOOK).key_patterns || []}
                      onDontPatternsChange={(patterns) =>
                        updateDontListByCategory(CATEGORY_HEADERS.HOOK, "key_patterns", patterns)
                      }
                    />

                    <div className="w-full h-px bg-gray-200" />

                    {/* Selling Points */}
                    <GuidelineSection
                      icon={<Tag01 className="h-6 w-6 text-gray-400" />}
                      title="Selling points"
                      category="Selling Points"
                      description={getDoListItem(CATEGORY_HEADERS.SELLING_POINTS).description || ""}
                      onDescriptionChange={(value) =>
                        updateDoListByCategory(CATEGORY_HEADERS.SELLING_POINTS, "description", value)
                      }
                      doPatterns={getDoListItem(CATEGORY_HEADERS.SELLING_POINTS).key_patterns || []}
                      onDoPatternsChange={(patterns) =>
                        updateDoListByCategory(CATEGORY_HEADERS.SELLING_POINTS, "key_patterns", patterns)
                      }
                      dontPatterns={getDontListItem(CATEGORY_HEADERS.SELLING_POINTS).key_patterns || []}
                      onDontPatternsChange={(patterns) =>
                        updateDontListByCategory(CATEGORY_HEADERS.SELLING_POINTS, "key_patterns", patterns)
                      }
                    />

                    <div className="w-full h-px bg-gray-200" />

                    {/* Sales Tactics */}
                    <GuidelineSection
                      icon={<Sale03 className="h-6 w-6 text-gray-400" />}
                      title="Sales Tactics"
                      category="Sales Tactics"
                      description={getDoListItem(CATEGORY_HEADERS.SALES_TACTICS).description || ""}
                      onDescriptionChange={(value) =>
                        updateDoListByCategory(CATEGORY_HEADERS.SALES_TACTICS, "description", value)
                      }
                      doPatterns={getDoListItem(CATEGORY_HEADERS.SALES_TACTICS).key_patterns || []}
                      onDoPatternsChange={(patterns) =>
                        updateDoListByCategory(CATEGORY_HEADERS.SALES_TACTICS, "key_patterns", patterns)
                      }
                      dontPatterns={getDontListItem(CATEGORY_HEADERS.SALES_TACTICS).key_patterns || []}
                      onDontPatternsChange={(patterns) =>
                        updateDontListByCategory(CATEGORY_HEADERS.SALES_TACTICS, "key_patterns", patterns)
                      }
                    />
                  </div>
                </div>

                {/* Divider before examples */}
                <div className="w-full h-px bg-gray-200" />

                {/* Good Examples - always show in edit mode */}
                <div className="w-full max-w-[768px] min-w-[560px] rounded-lg flex flex-col justify-start items-start gap-5">
                  <div className="self-stretch flex flex-col justify-start items-start gap-2">
                    <div className="self-stretch inline-flex justify-start items-start gap-2">
                      <CheckCircleBroken className="w-6 h-6 text-gray-400" />
                      <div className="justify-start text-quaternary text-base font-medium leading-6">Good examples</div>
                    </div>
                  </div>
                  {examples.length > 0 ? (
                    <div className="self-stretch flex-1 min-w-[560px] inline-flex justify-start items-start gap-8 flex-wrap content-start">
                      {examples.slice(0, 2).map((video: VideoExample) => (
                        <div key={video.videoId} className="inline-flex flex-col justify-start items-start gap-4">
                          <VideoEmbed url={video.videoUrl} thumbnail={video.thumbnail} />
                          <div className="self-stretch justify-start text-primary text-lg font-semibold leading-7">
                            @{video.creatorName}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="self-stretch p-6 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-center">
                      <p className="text-tertiary text-base">No videos were found as good examples for this product.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-gray-200" />

            {/* Things to Avoid Section - always show in edit mode */}
            <div className="self-stretch inline-flex justify-start items-start gap-6">
              <div className="w-96 inline-flex flex-col justify-start items-start gap-8">
                <div className="w-72 justify-start text-primary text-xl font-medium leading-8">
                  Things to avoid in your videos
                </div>
                <div className="inline-flex justify-start items-start gap-2">
                  <div className="self-stretch inline-flex flex-col justify-start items-center gap-2">
                    <AnnotationInfo className="w-5 h-5 text-gray-700" />
                    <div className="w-1 flex-1 bg-quaternary rounded-md" />
                  </div>
                  <p className="w-72 justify-start text-secondary text-base font-normal leading-6 italic">
                    Complying with our brand safety standards increases the likelihood that your content will get
                    boosted
                  </p>
                </div>
              </div>
              <div className="flex-1 flex flex-col justify-start items-start gap-12">
                <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                  <TextAreaBase
                    value={content.safetyList || ""}
                    onChange={(e) => updateContentField("safetyList", e.target.value)}
                    placeholder=""
                    className="self-stretch min-h-[360px]"
                  />
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-gray-200" />

            {/* More Info Section */}
            <div className="self-stretch inline-flex justify-start items-start gap-6">
              <div className="w-96 inline-flex flex-col justify-start items-start gap-8">
                <div className="w-72 justify-start text-primary text-xl font-medium leading-8">More Info</div>
              </div>
              <div className="flex-1 flex flex-col gap-4">
                {/* Tip box */}
                <div className="self-stretch p-6 bg-gray-50 rounded-md inline-flex flex-col justify-start items-start gap-2">
                  <div className="self-stretch justify-start text-primary text-base font-normal leading-6">
                    Want to add more context that is not shown above? Add in the custom field below.
                  </div>
                  <div className="self-stretch justify-start text-primary text-sm font-medium leading-5">
                    Tip: use bullets for clearer descriptions
                  </div>
                </div>
                <TextAreaBase
                  value={content.brief || ""}
                  onChange={(e) => updateContentField("brief", e.target.value)}
                  placeholder=""
                  className="w-full min-h-[120px]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
